/**
 * 인라인 콘텐츠 직렬화 (P0-①)
 * BlockNote inline content(styled-text 배열) ↔ contenteditable HTML 양방향 변환.
 *
 * BlockNote 형식:
 *   텍스트: { type:"text", text, styles:{ bold?, italic?, underline?, strike?, code?,
 *            textColor?, backgroundColor?, fontSize? } }
 *   링크:   { type:"link", href, content:[<텍스트>] }
 *   기본색(textColor/backgroundColor === "default")은 styles에 저장하지 않는다.
 *
 * 인라인 태그 규약(샘플 block-editor.html의 cleanInline과 정합 + lumir 확장):
 *   bold=<strong>, italic=<em>, underline=<u>, strike=<s>, code=<code>,
 *   textColor=<span data-text-color>, backgroundColor=<span data-background-color>,
 *   fontSize=<span data-font-size style="font-size:..">, link=<a href>.
 *
 * 라운드트립 보증: 정규화된 입력(인접 동일스타일 run 없음, 빈 텍스트 run 없음)에 대해
 *   inlineContentToHtml → htmlToInlineContent 가 항등(deep-equal).
 *
 * 주의: fontSize는 여기서 styles.fontSize로 다룬다. 저장 JSON의 "형제키" 변환
 *   (lower/liftFontSize)은 문서 경계(직렬화 레이어 상위, P0-④)에서 적용한다.
 */

import { neutralizeUrl } from "../utils/url.js";

const DEFAULT_COLOR = "default";

function escapeHtml(s) {
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function escapeAttr(s) {
  return String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

/** styled-text 한 항목 → HTML. 중첩 순서 고정(외→내): textColor > bg > fontSize > bold > italic > underline > strike > code */
function styledTextToHtml(node) {
  // HardBreak: 모델의 "\n"(줄바꿈)을 <br>로 렌더(라운드트립: walkInline에서 <br>→"\n")
  let html = escapeHtml(node && node.text != null ? node.text : "").replace(/\n/g, "<br>");
  const s = (node && node.styles) || {};
  if (s.code) html = `<code>${html}</code>`;
  if (s.strike) html = `<s>${html}</s>`;
  if (s.underline) html = `<u>${html}</u>`;
  if (s.italic) html = `<em>${html}</em>`;
  if (s.bold) html = `<strong>${html}</strong>`;
  if (s.fontSize)
    html = `<span data-font-size="${escapeAttr(s.fontSize)}" style="font-size:${escapeAttr(s.fontSize)}">${html}</span>`;
  if (s.backgroundColor && s.backgroundColor !== DEFAULT_COLOR)
    html = `<span data-background-color="${escapeAttr(s.backgroundColor)}">${html}</span>`;
  if (s.textColor && s.textColor !== DEFAULT_COLOR)
    html = `<span data-text-color="${escapeAttr(s.textColor)}">${html}</span>`;
  return html;
}

/** 인라인 체크박스(표 셀 원자) → HTML. contenteditable=false 독립 span, 텍스트 자식 없음. */
function checkboxToHtml(node) {
  const on = !!(node && node.checked);
  const v = on ? "true" : "false";
  return (
    `<span class="lumir-cell-check${on ? " checked" : ""}" role="checkbox"` +
    ` aria-checked="${v}" contenteditable="false" data-lumir-checkbox="1" data-checked="${v}"></span>`
  );
}

/** 셀 내 순서목록/불렛목록 마커(인라인 원자). 번호는 위치 기반이라 미저장 — CSS 카운터로 표시. */
function olItemToHtml() {
  return `<span class="lumir-cell-ol" contenteditable="false" data-lumir-ol="1"></span>`;
}
function ulItemToHtml() {
  return `<span class="lumir-cell-ul" contenteditable="false" data-lumir-ul="1"></span>`;
}

function inlineNodeToHtml(node) {
  if (node && node.type === "checkbox") return checkboxToHtml(node);
  if (node && node.type === "olItem") return olItemToHtml();
  if (node && node.type === "ulItem") return ulItemToHtml();
  if (node && node.type === "link") {
    const inner = (node.content || []).map(styledTextToHtml).join("");
    return `<a href="${escapeAttr(neutralizeUrl(node.href))}">${inner}</a>`;
  }
  return styledTextToHtml(node);
}

/** BlockNote inline content 배열 → contenteditable HTML 문자열 */
export function inlineContentToHtml(content) {
  if (!Array.isArray(content)) return "";
  return content.map(inlineNodeToHtml).join("");
}

const TAG_MARK = {
  strong: "bold",
  b: "bold",
  em: "italic",
  i: "italic",
  u: "underline",
  s: "strike",
  strike: "strike",
  del: "strike",
  code: "code",
};

function readSpanStyles(el, styles) {
  const tc = el.getAttribute("data-text-color");
  if (tc) styles.textColor = tc;
  const bc = el.getAttribute("data-background-color");
  if (bc) styles.backgroundColor = bc;
  const fs = el.getAttribute("data-font-size") || (el.style && el.style.fontSize) || "";
  if (fs) styles.fontSize = fs;
}

function sameStyles(a, b) {
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  return ak.every((k) => a[k] === b[k]);
}

/** 인접한 동일-스타일 텍스트 run 병합(BlockNote 정규화와 정합) */
function mergeAdjacentText(items) {
  const out = [];
  for (const it of items) {
    const prev = out[out.length - 1];
    if (prev && prev.type === "text" && it.type === "text" && sameStyles(prev.styles, it.styles)) {
      prev.text += it.text;
    } else {
      out.push(it);
    }
  }
  return out;
}

function walkInline(parent, styles, out, collapseWs) {
  parent.childNodes.forEach((child) => {
    if (child.nodeType === 3) {
      // text node — ZWSP(캐럿 앵커 ​)는 직렬화에서 제거(인라인 코드 탈출용 보조 문자)
      let text = child.nodeValue.replace(/​/g, "");
      // 붙여넣기 경로(collapseWs)에서만: 외부(워드 등) HTML은 소스 정렬용 개행이 텍스트 노드에 남아,
      // 하드브레이크(<br>=\n)와 겹쳐 이중 줄바꿈이 되거나 번호/본문이 갈라진다. HTML 렌더 규칙대로
      // 개행(+인접 공백)을 단일 공백으로 접는다. (에디터 자체 직렬화·코드블록의 \n은 보존해야 하므로 미적용)
      if (collapseWs) text = text.replace(/[ \t]*\r?\n[ \t\r\n]*/g, " ");
      if (text) out.push({ type: "text", text, styles: { ...styles } });
    } else if (child.nodeType === 1) {
      // element
      const tag = child.tagName.toLowerCase();
      // 인라인 원자(표 셀 체크박스/순서·불렛 마커): 자식 재귀 없이 노드로 회수(라운드트립). span 일반 처리보다 먼저.
      if (child.getAttribute && child.getAttribute("data-lumir-checkbox")) {
        out.push({ type: "checkbox", checked: child.getAttribute("data-checked") === "true" });
        return; // forEach 콜백 return = 이 child 건너뜀(내부 미탐색)
      }
      if (child.getAttribute && child.getAttribute("data-lumir-ol")) {
        out.push({ type: "olItem" });
        return;
      }
      if (child.getAttribute && child.getAttribute("data-lumir-ul")) {
        out.push({ type: "ulItem" });
        return;
      }
      if (tag === "a") {
        const inner = [];
        walkInline(child, styles, inner, collapseWs);
        out.push({
          type: "link",
          href: child.getAttribute("href") || "",
          content: mergeAdjacentText(inner),
        });
      } else if (tag === "br") {
        // HardBreak caret용 보조 <br>(filler)는 직렬화에서 제외(라운드트립 무오염)
        if (child.hasAttribute && child.hasAttribute("data-lumir-br-filler")) return;
        out.push({ type: "text", text: "\n", styles: { ...styles } });
      } else {
        const next = { ...styles };
        const mark = TAG_MARK[tag];
        if (mark) next[mark] = true;
        if (tag === "span") readSpanStyles(child, next);
        walkInline(child, next, out, collapseWs);
      }
    }
  });
}

/**
 * contenteditable HTML(문자열 또는 컨테이너 Element) → BlockNote inline content 배열.
 * 문자열을 넘기면 DOM(document) 환경이 필요하다.
 */
export function htmlToInlineContent(html, opts) {
  let container;
  if (typeof html === "string") {
    if (typeof document === "undefined") {
      throw new Error("htmlToInlineContent: 문자열 파싱에는 DOM(document)이 필요합니다");
    }
    container = document.createElement("div");
    container.innerHTML = html;
  } else {
    container = html; // 이미 Element
  }
  const out = [];
  walkInline(container, {}, out, !!(opts && opts.collapseWhitespace));
  return mergeAdjacentText(out);
}
